import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSButton from "views/system-design/DSButton";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSAlert from "views/system-design/DSAlert";
import DSEnumSelect from "views/system-design/DSEnumSelect";
import DSIconButton from "views/system-design/DSIconButton";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import {
  KlydoMonitor,
  MonitorStatus,
  MonitorType,
  SilencedTarget,
} from "../../../api/admin";
import { useAlertPolicies } from "../../AlertPolicies/hooks/useAlertPolicies";
import {
  useUpdateMonitor,
  useDeleteMonitor,
  useSnoozeMonitor,
  usePauseMonitor,
  useResumeMonitor,
  useTriggerMonitor,
  useAcknowledgeMonitor,
  useResolveMonitor,
} from "../hooks/useKlydoMonitors";
import useUserQuery from "views/Collection/hooks/useUserQuery";
import MonitorStatusChip from "./MonitorStatusChip";
import SilencedTargetsEditor from "./SilencedTargetsEditor";
import OffHoursAlertingPicker from "./OffHoursAlertingPicker";
import ClocksErrorsTable from "./ClocksErrorsTable";
import { incidentClockIds } from "../incidentClockIds";

// The cron monitor that fails when the current clock firmware has un-ignored
// errors (backend @Monitor('clocks-errors')). Its own document only records a
// generic message, so the modal lists the actual failing errors separately.
const CLOCKS_ERRORS_MONITOR_NAME = "clocks-errors";

// Enough to see the shape of an incident without laying out megabytes of it.
const MAX_SHOWN_REPORTS = 5;
const MAX_SHOWN_ISSUES = 50;
const MAX_SHOWN_CHARS = 20000;

interface MonitorDetailsModalProps {
  monitor: KlydoMonitor | null;
  open: boolean;
  onClose: () => void;
}

const MonitorDetailsModal = ({
  monitor,
  open,
  onClose,
}: MonitorDetailsModalProps) => {
  const navigate = useNavigate();
  const [name, setName] = useState(monitor?.name ?? "");
  const [intervalHours, setIntervalHours] = useState(
    monitor?.intervalHours ?? 1,
  );
  const [maxErrors, setMaxErrors] = useState(
    monitor?.maxTemporaryErrorsBeforeAlert ?? 1,
  );
  const [alertPolicyId, setAlertPolicyId] = useState(
    monitor?.alertPolicyId ?? "",
  );
  const [alertOutsideWorkHours, setAlertOutsideWorkHours] = useState(
    monitor?.alertOutsideWorkHours ?? false,
  );
  const [chunkSize, setChunkSize] = useState(monitor?.chunkSize ?? "");
  const [startHour, setStartHour] = useState(monitor?.startHour ?? "");
  const [fromVersion, setFromVersion] = useState(
    monitor?.fromVersion?.toString() ?? "",
  );
  const [silencedTargets, setSilencedTargets] = useState<SilencedTarget[]>(
    monitor?.silencedTargets ?? [],
  );
  const [runChunkIndex, setRunChunkIndex] = useState("");
  const [snoozeDuration, setSnoozeDuration] = useState(48);
  const [fullScreen, setFullScreen] = useState(false);

  const isClockInternal = monitor?.type === MonitorType.ClockInternal;
  const isClocksErrorsMonitor = monitor?.name === CLOCKS_ERRORS_MONITOR_NAME;

  const { data: alertPolicies } = useAlertPolicies();
  const policyOptions = [
    { value: "", label: "— None —" },
    ...(alertPolicies?.map((p) => ({ value: p.id, label: p.name })) ?? []),
  ];
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [triggered, setTriggered] = useState(false);
  const [copiedErrors, setCopiedErrors] = useState(false);

  const { mutate: update, isPending: isUpdating } = useUpdateMonitor();
  const { mutate: remove, isPending: isDeleting } = useDeleteMonitor();
  const { mutate: snooze, isPending: isSnoozing } = useSnoozeMonitor();
  const { mutate: pause, isPending: isPausing } = usePauseMonitor();
  const { mutate: resume, isPending: isResuming } = useResumeMonitor();
  const { mutate: trigger, isPending: isTriggering } = useTriggerMonitor();
  const { mutate: acknowledge, isPending: isAcknowledging } =
    useAcknowledgeMonitor();
  const { mutate: resolve, isPending: isResolving } = useResolveMonitor();

  const { data: acknowledgedByUser } = useUserQuery(monitor?.acknowledge?.by);
  const { data: resolvedByUser } = useUserQuery(
    monitor?.resolved?.by !== "auto" ? monitor?.resolved?.by : undefined,
  );
  const acknowledgedByName =
    acknowledgedByUser?.name ?? monitor?.acknowledge?.by;
  const resolvedByName = resolvedByUser?.name ?? monitor?.resolved?.by;

  useEffect(() => {
    if (monitor) {
      setName(monitor.name);
      setIntervalHours(monitor.intervalHours ?? 1);
      setMaxErrors(monitor.maxTemporaryErrorsBeforeAlert ?? 1);
      setAlertPolicyId(monitor.alertPolicyId ?? "");
      setAlertOutsideWorkHours(monitor.alertOutsideWorkHours ?? false);
      setChunkSize(monitor.chunkSize ?? "");
      setStartHour(monitor.startHour ?? "");
      setFromVersion(monitor.fromVersion?.toString() ?? "");
      setSilencedTargets(monitor.silencedTargets ?? []);
      setTriggered(false);
      setConfirmDelete(false);
      setFullScreen(false);
    }
    // Deliberately keyed on the monitor's identity and the dialog opening, not
    // on the monitor object: the list behind this dialog refetches every 30s
    // and hands down a new object each time, which on [monitor] wiped whatever
    // was being edited — a half-typed interval, a chip just added. Reopening
    // the dialog is what re-seeds from the server.
  }, [monitor?.id, open]);

  if (!monitor) return null;

  const isSnoozed =
    monitor.snoozeUntil !== null && Date.now() < monitor.snoozeUntil;
  // `active` defaults to true server-side; only an explicit false means
  // paused (older docs may omit the field entirely).
  const isPaused = monitor.active === false;

  // Clock-internal monitors have no incidentTargets — every report names its
  // clock, so the distinct reporters are the mute candidates.
  const incidentTargets = isClockInternal
    ? Array.from(
        new Set((monitor.failureReports ?? []).map((report) => report.clockId)),
      )
    : (monitor.incidentTargets ?? []);

  const failingClocks = incidentClockIds(monitor).length;

  const hasErrors =
    (monitor.issues?.length ?? 0) > 0 ||
    (monitor.failureReports?.length ?? 0) > 0 ||
    !!monitor.lastError;
  // A clock-internal monitor keeps up to 100 failure reports, each carrying a
  // context and a device snapshot — on a fleet-wide incident that is megabytes
  // of JSON. Laying that out as wrapped text costs half a second per reflow,
  // and React reflows it on every keystroke, which froze the dialog outright.
  // What is shown is capped; Copy still builds the whole thing on demand,
  // because building a big string is cheap and only rendering it is not.
  const buildErrorsJson = (full: boolean) => {
    const reports = monitor.failureReports ?? [];
    const issues = monitor.issues ?? [];
    return JSON.stringify(
      isClockInternal
        ? {
            lastError: monitor.lastError,
            ...(full || reports.length <= MAX_SHOWN_REPORTS
              ? {}
              : {
                  showing: `most recent ${MAX_SHOWN_REPORTS} of ${reports.length} reports — Copy for all`,
                }),
            failureReports: full ? reports : reports.slice(-MAX_SHOWN_REPORTS),
          }
        : {
            ...(full || issues.length <= MAX_SHOWN_ISSUES
              ? {}
              : {
                  showing: `first ${MAX_SHOWN_ISSUES} of ${issues.length} issues — Copy for all`,
                }),
            issues: full ? issues : issues.slice(0, MAX_SHOWN_ISSUES),
            // The silenceable ids, alongside the rendered lines they came from.
            incidentTargets: monitor.incidentTargets ?? [],
            lastError: monitor.lastError,
          },
      null,
      2,
    );
  };

  // A backstop for the shapes the caps above do not bound — one issue string
  // holding thousands of ids, or a single enormous device snapshot.
  const shown = buildErrorsJson(false);
  const errorsJson =
    shown.length > MAX_SHOWN_CHARS
      ? `${shown.slice(0, MAX_SHOWN_CHARS)}\n… truncated — Copy for the full details`
      : shown;

  const handleCopyErrors = async () => {
    try {
      await navigator.clipboard.writeText(buildErrorsJson(true));
      setCopiedErrors(true);
      setTimeout(() => setCopiedErrors(false), 2000);
    } catch {
      // Clipboard API unavailable (e.g. non-secure context) — ignore.
    }
  };

  // One field at a time. Text and number fields commit on blur rather than on
  // every keystroke — typing "24" into the interval would otherwise save a 2
  // on the way past — while discrete controls (the policy select, the silenced
  // chips) commit the moment they change.
  const save = (data: Partial<KlydoMonitor>) => {
    setSaveError(null);
    update(
      { id: monitor.id, data },
      {
        onSuccess: (saved: KlydoMonitor) => {
          setSavedAt(Date.now());
          setTimeout(() => setSavedAt(null), 2000);
          // The server stamps who silenced a target and when, so take the
          // chips back from the response — otherwise they would keep showing
          // as unsaved until the dialog was reopened.
          if (data.silencedTargets) {
            setSilencedTargets(saved.silencedTargets ?? []);
          }
        },
        onError: (error: unknown) =>
          setSaveError(
            error instanceof Error ? error.message : "Could not save",
          ),
      },
    );
  };

  // Commits only when the value actually changed, so tabbing through the form
  // does not write.
  const saveIfChanged = (data: Partial<KlydoMonitor>, current: unknown) => {
    const [value] = Object.values(data);
    if (value === current) return;
    save(data);
  };

  // "" clears an optional number back to null; anything unparseable is left to
  // the field rather than written as NaN.
  const optionalNumber = (raw: string): number | null | undefined => {
    if (raw.trim() === "") return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const handleSnooze = () => {
    snooze({ id: monitor.id, durationHours: snoozeDuration });
  };

  const handleTogglePause = () => {
    if (isPaused) resume(monitor.id);
    else pause(monitor.id);
  };

  const handleDelete = () => {
    remove(monitor.id, { onSuccess: onClose });
  };

  return (
    <DSDialog
      open={open}
      onClose={onClose}
      maxWidth={isClocksErrorsMonitor ? "lg" : "sm"}
      fullWidth
      fullScreen={fullScreen}
    >
      <DSDialogTitle>
        <DSBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <MonitorStatusChip status={monitor.status} />
          {isUpdating && (
            <DSTypography variant="caption" color="text.secondary">
              Saving…
            </DSTypography>
          )}
          {!isUpdating && savedAt && (
            <DSTypography variant="caption" color="success.main">
              ✓ Saved
            </DSTypography>
          )}
          {isPaused && (
            <DSTypography variant="caption" color="text.secondary">
              ⏸ Paused
            </DSTypography>
          )}
          {isSnoozed && (
            <DSTypography variant="caption" color="text.secondary">
              Snoozed until{" "}
              {new Date(monitor.snoozeUntil!).toLocaleDateString()}
            </DSTypography>
          )}
          {monitor.acknowledge && (
            <DSTypography variant="caption" color="warning.main">
              Acknowledged by {acknowledgedByName} at{" "}
              {new Date(monitor.acknowledge.at).toLocaleString()}
            </DSTypography>
          )}
          {monitor.resolved && (
            <DSTypography variant="caption" color="success.main">
              {monitor.resolved.by === "auto"
                ? `Automatically resolved at ${new Date(monitor.resolved.at).toLocaleString()}`
                : `Resolved by ${resolvedByName} at ${new Date(monitor.resolved.at).toLocaleString()}`}
            </DSTypography>
          )}
          {/* Acknowledge / Resolve */}
          {monitor.status === MonitorStatus.Failed && (
            <DSBox sx={{ display: "flex", gap: 1 }}>
              {!monitor.acknowledge && (
                <DSButton
                  variant="outlined"
                  color="warning"
                  size="small"
                  disabled={isAcknowledging}
                  onClick={() => acknowledge(monitor.id)}
                >
                  🖐 Acknowledge
                </DSButton>
              )}
              {!monitor.acknowledge && !monitor.resolved && (
                <DSButton
                  variant="outlined"
                  color="success"
                  size="small"
                  disabled={isAcknowledging || isResolving}
                  onClick={() =>
                    acknowledge(monitor.id, {
                      onSuccess: () => resolve(monitor.id),
                    })
                  }
                >
                  🖐✅ Acknowledge & Resolve
                </DSButton>
              )}
              {monitor.acknowledge && !monitor.resolved && (
                <DSButton
                  variant="outlined"
                  color="success"
                  size="small"
                  disabled={isResolving}
                  onClick={() => resolve(monitor.id)}
                >
                  ✅ Resolve
                </DSButton>
              )}
            </DSBox>
          )}
          {isClocksErrorsMonitor && (
            <DSIconButton
              size="small"
              onClick={() => setFullScreen((prev) => !prev)}
              title={fullScreen ? "Exit full screen" : "Expand to full screen"}
              sx={{ ml: "auto" }}
            >
              {fullScreen ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </DSIconButton>
          )}
        </DSBox>
      </DSDialogTitle>

      <DSDialogContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {saveError && (
            <DSAlert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </DSAlert>
          )}
          {/* Read-only info */}
          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {isClockInternal ? (
              <>
                <DSTypography variant="caption" color="text.secondary">
                  Last reported by:{" "}
                  {monitor.failureReports?.length
                    ? monitor.failureReports[monitor.failureReports.length - 1]
                        .clockId
                    : "Never"}
                </DSTypography>
                <DSTypography variant="caption" color="text.secondary">
                  Failure reports (this incident):{" "}
                  {monitor.failureReports?.length ?? 0}
                </DSTypography>
                <DSTypography variant="caption" color="text.secondary">
                  Last failure:{" "}
                  {monitor.lastFailedAt
                    ? new Date(monitor.lastFailedAt).toLocaleString()
                    : "Never"}
                </DSTypography>
              </>
            ) : (
              <>
                <DSTypography variant="caption" color="text.secondary">
                  Last run:{" "}
                  {monitor.lastRunAt
                    ? new Date(monitor.lastRunAt).toLocaleString()
                    : "Never"}
                </DSTypography>
                <DSTypography variant="caption" color="text.secondary">
                  Last success:{" "}
                  {monitor.lastSuccessAt
                    ? new Date(monitor.lastSuccessAt).toLocaleString()
                    : "Never"}
                </DSTypography>
                {monitor.runDurationMs && (
                  <DSTypography variant="caption" color="text.secondary">
                    Last duration: {(monitor.runDurationMs / 1000).toFixed(1)}s
                  </DSTypography>
                )}
              </>
            )}
            <DSTypography variant="caption" color="text.secondary">
              Consecutive failures: {monitor.consecutiveFailures}
            </DSTypography>
          </DSBox>

          {/* Errors JSON */}
          {hasErrors && (
            <DSBox>
              <DSBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <DSTypography variant="caption" color="error">
                  Errors / Issues:
                </DSTypography>
                <DSButton
                  size="small"
                  variant="outlined"
                  onClick={handleCopyErrors}
                >
                  {copiedErrors ? "✓ Copied" : "Copy"}
                </DSButton>
              </DSBox>
              <pre
                style={{
                  background: "#161616",
                  borderRadius: 4,
                  padding: 12,
                  fontSize: 11,
                  overflowX: "auto",
                  maxHeight: 160,
                  overflowY: "auto",
                  margin: 0,
                  color: "#ef9a9a",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {errorsJson}
              </pre>
            </DSBox>
          )}

          {/* Per-target silencing. Hidden for the monitors that report an
              aggregate (a rate, a count), where there is no entity to mute. */}
          {monitor.targetType && (
            <SilencedTargetsEditor
              targetType={monitor.targetType}
              silencedTargets={silencedTargets}
              onChange={(next) => {
                setSilencedTargets(next);
                save({ silencedTargets: next });
              }}
              incidentTargets={incidentTargets}
              silencedHits={monitor.silencedHits ?? []}
            />
          )}

          {/* Editable fields */}
          <DSTextField
            label="Name"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              if (!name.trim()) {
                setName(monitor.name);
                return;
              }
              saveIfChanged({ name: name.trim() }, monitor.name);
            }}
          />
          {isClockInternal ? (
            <DSTextField
              label="From version (optional)"
              type="number"
              size="small"
              value={fromVersion}
              onChange={(e) => setFromVersion(e.target.value)}
              onBlur={() => {
                const parsed = optionalNumber(fromVersion);
                if (parsed === undefined) return;
                saveIfChanged({ fromVersion: parsed }, monitor.fromVersion);
              }}
              helperText="Reports from clocks below this version are ignored. Empty accepts all."
            />
          ) : (
            <>
              <DSTextField
                label="Interval (hours)"
                type="number"
                size="small"
                value={intervalHours}
                onChange={(e) => setIntervalHours(Number(e.target.value))}
                onBlur={() => {
                  if (!Number.isFinite(intervalHours) || intervalHours <= 0) {
                    setIntervalHours(monitor.intervalHours ?? 1);
                    return;
                  }
                  saveIfChanged({ intervalHours }, monitor.intervalHours);
                }}
              />
              <DSTextField
                label="Max temporary errors before alert"
                type="number"
                size="small"
                value={maxErrors}
                onChange={(e) => setMaxErrors(Number(e.target.value))}
                onBlur={() => {
                  if (!Number.isFinite(maxErrors) || maxErrors < 1) {
                    setMaxErrors(monitor.maxTemporaryErrorsBeforeAlert ?? 1);
                    return;
                  }
                  saveIfChanged(
                    { maxTemporaryErrorsBeforeAlert: maxErrors },
                    monitor.maxTemporaryErrorsBeforeAlert,
                  );
                }}
              />
              {monitor.chunked && (
                <DSTextField
                  label="Chunk size (klydos per run)"
                  type="number"
                  size="small"
                  value={chunkSize}
                  inputProps={{ min: 1 }}
                  onChange={(e) => setChunkSize(e.target.value)}
                  onBlur={() => {
                    const parsed = optionalNumber(String(chunkSize));
                    if (parsed === undefined) return;
                    saveIfChanged({ chunkSize: parsed }, monitor.chunkSize);
                  }}
                />
              )}
              <DSTextField
                label="Start hour (0-23, UTC)"
                type="number"
                size="small"
                value={startHour}
                inputProps={{ min: 0, max: 23 }}
                onChange={(e) => setStartHour(e.target.value)}
                onBlur={() => {
                  const parsed = optionalNumber(String(startHour));
                  if (parsed === undefined) return;
                  if (parsed !== null && (parsed < 0 || parsed > 23)) {
                    setStartHour(monitor.startHour ?? "");
                    return;
                  }
                  saveIfChanged({ startHour: parsed }, monitor.startHour);
                }}
              />
            </>
          )}
          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <DSTypography variant="caption" color="text.secondary">
              Alert policy
            </DSTypography>
            <DSEnumSelect
              size="small"
              value={alertPolicyId}
              options={policyOptions}
              onChange={(value) => {
                setAlertPolicyId(value);
                save({ alertPolicyId: value || null });
              }}
            />
          </DSBox>
          <OffHoursAlertingPicker
            value={alertOutsideWorkHours}
            onChange={(value) => {
              setAlertOutsideWorkHours(value);
              save({ alertOutsideWorkHours: value });
            }}
          />

          {/* Snooze */}
          <DSBox sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <DSTextField
              label="Snooze duration (hours)"
              type="number"
              size="small"
              value={snoozeDuration}
              onChange={(e) => setSnoozeDuration(Number(e.target.value))}
              sx={{ flex: 1 }}
            />
            <DSButton
              variant="outlined"
              onClick={handleSnooze}
              disabled={isSnoozing}
              size="small"
            >
              {isSnoozed ? "Re-snooze" : "Snooze"}
            </DSButton>
          </DSBox>

          {/* Pause / Resume */}
          <DSBox
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <DSTypography variant="caption" color="text.secondary">
              {isClockInternal
                ? isPaused
                  ? "Monitor is inactive — clock failure reports are ignored until resumed."
                  : "Pausing marks this monitor inactive: clock failure reports are ignored until resumed."
                : isPaused
                  ? "Monitor is paused — it will not run or alert until resumed."
                  : "Pausing stops this monitor from running and alerting until resumed."}
            </DSTypography>
            <DSButton
              variant="outlined"
              color={isPaused ? "success" : "warning"}
              onClick={handleTogglePause}
              disabled={isPausing || isResuming}
              size="small"
            >
              {isPaused ? "▶ Resume" : "⏸ Pause"}
            </DSButton>
          </DSBox>

          {/* The clocks-errors monitor's document only records a generic
              message, so list the actual failing errors with their per-clock
              and per-version ignore actions. */}
          {isClocksErrorsMonitor && <ClocksErrorsTable />}
        </DSBox>
      </DSDialogContent>

      <DSDialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <DSBox sx={{ display: "flex", gap: 1 }}>
          {confirmDelete ? (
            <>
              <DSTypography variant="body2" color="error" sx={{ mr: 1 }}>
                Are you sure?
              </DSTypography>
              <DSButton
                color="error"
                variant="contained"
                onClick={handleDelete}
                disabled={isDeleting}
                size="small"
              >
                Confirm
              </DSButton>
              <DSButton onClick={() => setConfirmDelete(false)} size="small">
                Cancel
              </DSButton>
            </>
          ) : (
            <DSButton
              color="error"
              onClick={() => setConfirmDelete(true)}
              size="small"
            >
              Delete
            </DSButton>
          )}
        </DSBox>
        {monitor.chunked && (
          <DSBox sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <DSTypography variant="caption" color="text.secondary">
              Next chunk {monitor.config?.chunkIndex ?? 0} of{" "}
              {monitor.config?.totalChunks ?? "?"}
            </DSTypography>
            <DSTextField
              label="Re-run chunk #"
              type="number"
              size="small"
              value={runChunkIndex}
              onChange={(e) => setRunChunkIndex(e.target.value)}
            />
            <DSButton
              variant="outlined"
              size="small"
              disabled={runChunkIndex === "" || isTriggering}
              onClick={() =>
                trigger(
                  { id: monitor.id, chunkIndex: Number(runChunkIndex) },
                  {
                    onSuccess: () => {
                      setTriggered(true);
                      setTimeout(() => setTriggered(false), 3000);
                    },
                  },
                )
              }
            >
              Run chunk
            </DSButton>
          </DSBox>
        )}
        <DSBox sx={{ display: "flex", gap: 1 }}>
          {failingClocks > 0 && (
            <DSButton
              variant="outlined"
              color="inherit"
              size="small"
              sx={{ color: "text.secondary", borderColor: "divider" }}
              onClick={() => navigate(`/admin/devices?monitor=${monitor.id}`)}
            >
              View {failingClocks} failing clock{failingClocks > 1 ? "s" : ""}
            </DSButton>
          )}
          {!isClockInternal && (
            <DSButton
              variant="outlined"
              onClick={() =>
                trigger(
                  { id: monitor.id },
                  {
                    onSuccess: () => {
                      setTriggered(true);
                      setTimeout(() => setTriggered(false), 3000);
                    },
                  },
                )
              }
              disabled={isTriggering || triggered}
              size="small"
            >
              {triggered ? "✓ Triggered!" : "Trigger Now"}
            </DSButton>
          )}
          <DSButton variant="contained" onClick={onClose} size="small">
            Close
          </DSButton>
        </DSBox>
      </DSDialogActions>
    </DSDialog>
  );
};

export default MonitorDetailsModal;
